import { formatNumber as kitchenFormatNumber } from '@thefarmersfront/kitchen-utensil';

import { TypeCodeState, TypeSelectOption } from '../types';

/** @description number 타입 input에 숫자가 아닌 다른 문자는 입력되지 못하게 하는 함수 */
export const blockNonNumberKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e): void => {
  const regex = /^[0-9\b]$/;

  if (!regex.test(e.key) && e.key !== 'Backspace') {
    e.preventDefault();
  }
};

/** @description 숫자를 두자리 문자열 포맷으로 변경해주는 함수 */
export const formatNumberToTwoDigits = (number: number): string => {
  return number.toString().padStart(2, '0');
};

/** @description 주어진 스트링이 json 포맷의 문자열인지 체크해주는 함수 */
export const checkIsJsonString = (str: string): boolean => {
  try {
    const json = JSON.parse(str);
    return typeof json === 'object' && json !== null;
  } catch {
    return false;
  }
};

/** @description base64 문자열을 Blob 객체로 변환 */
export const base64ToBlob = (base64: string): Blob => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray]);
};

/** @description Blob 객체를 File 객체로 변환 */
export const base64ToFile = (base64: string, filename: string): File => {
  const blob = base64ToBlob(base64);
  return new File([blob], filename);
};

/** @description base64 문자열을 Byte로 반환 */
const getBase64FileSize = (base64String: string): number => {
  const base64Length = base64String.replace(/^data:image\/\w+;base64,/, '').length;
  return Math.ceil((base64Length * 3) / 4);
};

/** @description base64 문자열을 이미지화 및 maxFileSize 이하로 크기 조정 후 data URL로 반환 */
export const resizeBase64Img = (base64: string, maxFileSize: number): Promise<string> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const img = document.createElement('img');
    img.src = base64;
    img.onload = function () {
      if (!context) {
        resolve('');
        return;
      }

      let { width, height } = img;

      let resizedDataURL = '';
      let currentFileSize = getBase64FileSize(base64);

      while (currentFileSize > maxFileSize) {
        width /= 2;
        height /= 2;

        canvas.width = width;
        canvas.height = height;

        context.clearRect(0, 0, width, height);
        context.drawImage(img, 0, 0, width, height);

        resizedDataURL = canvas.toDataURL();
        currentFileSize = getBase64FileSize(resizedDataURL);
      }

      resolve(resizedDataURL || base64);
    };
  });
};

/** @description 계약 체결 시, 주민등록번호에 하이픈을 추가하는 함수 */
export const formatIdentificationNumber = (value: string): string => {
  return value
    .replace(/[^\d]/g, '')
    .replace(/(\d{6})(\d)/, '$1-$2')
    .slice(0, 14);
};

/** @description 주민등록번호에 하이픈을 추가하고 뒷자리 6자리를 *로 마스킹하는 함수 */
export const getMaskedJuminNo = (value: string): string => {
  return value ? value.replace(/(\d{6})(\d{1})(\d{6})/, '$1-$2******') : '';
};

/** @description 객체 배열의 특정 키에 대해 중복을 제거하여 반환 */
export const getUniqueValues = <T>(array: T[], uniqueField: keyof T) => {
  const uniqueValues: Record<string, T[keyof T]> = {};

  array.forEach((item) => {
    const value = item?.[uniqueField];

    if (value === undefined || value === null) {
      return;
    }

    if (typeof value === 'string') {
      uniqueValues[value] = value;
    } else if (typeof value === 'object' && 'code' in value && typeof value.code === 'string') {
      uniqueValues[value.code] = value;
    }
  });

  return Object.values(uniqueValues);
};

/** @description 입력값이 숫자인 경우, 천 단위로 콤마를 찍어 문자열로 반환 */
export const formatNumber = (
  value: number | string,
  { unit = '', fallback = '-' }: { unit?: string; fallback?: string } = {}
) => {
  const result = kitchenFormatNumber(Number(value), { unit, fallback });

  return result ?? fallback;
};

/** @description 근무 시간을 주어진 포맷으로 변경해주는 함수 */
export const getWorkingTimeText = (workingHour: number, workingMinute: number): string => {
  if (!workingHour || workingHour === 0) {
    return `${workingMinute}분`;
  }

  return `${workingHour}시간 ${workingMinute}분`;
};

/** @description 코드 타입을 셀렉트 컴포넌트 옵션으로 변경해주는 함수 */
export const formatterCodeToSelectOption = (option: TypeCodeState | null): TypeSelectOption | null => {
  if (!option) {
    return null;
  }
  return {
    value: option.code,
    text: option.text,
  };
};
